import ko from "knockout";
import * as homeConfiguration from "flow/stores/homeConfiguration";
import manifestManager from "flow/utils/manifestManager";
import viewCache from "flow/utils/viewCache";
import { CloseCallback, Dictionary } from "flow/api/types";
import { ISortableItem } from "flow/koComponents/ko-sortable-list";
import viewHtml from "./HomeConfigurationDialog.html";
import config from "flow/config";
import { ICommand } from "flow/interfaces/ICommand";
import "flow/koComponents/ko-sortable-list";
import type MyFlow from "flow/MyFlow";
import environment from "flow/configuration/environment";
import { HomeConfig } from "flow/entities/HomeConfig";

// I create my own manifest
manifestManager.addManifests(config.moduleId, [
    {
        id: config.componentIds.homeConfiguration,
        isDialog: true,
        namespace: "default",
        reuseInstance: false,
        useFormValidation: false,
        viewModelId: "flow/viewModels/HomeConfigurationDialog",
        viewUrl: "virtual/HomeConfigurationDialog"
    }
]);

viewCache.inject("virtual/HomeConfigurationDialog", viewHtml);

export default class HomeConfigurationDialog {
    private closeCallback: CloseCallback;
    containerEntries = ko.observableArray<ISortableItem>(this.getSettings());

    componentTitle = ko.observable(this.myFlow.translate("FlowHtml.Translations.HomeConfiguration"));

    allVisible = ko.pureComputed(() => {
        return !this.containerEntries().some(entry => !entry.isVisible());
    });

    constructor(private readonly myFlow: MyFlow) {}

    activate(_viewContext: any, closeCallback: CloseCallback) {
        this.closeCallback = closeCallback;
    }

    getSettings() {
        return manifestManager
            .getHomeScreenManifests()
            .map((manifest: any) => ({
                id: manifest.id,
                name: manifest.name,
                isVisible: ko.observable(!homeConfiguration.isHidden(manifest.id)),
                order: ko.observable(homeConfiguration.getSortOrder(manifest.id, manifest.order))
            }))
            .sort((a: ISortableItem, b: ISortableItem) => a.order() - b.order());
    }

    async onSave() {
        const sortOrder: Dictionary<number> = {};
        this.containerEntries()
            .slice()
            .sort((a, b) => a.order() - b.order())
            .forEach((entry, index) => {
                sortOrder[entry.id] = index;
            });

        const blacklist = this.containerEntries()
            .filter(entry => !entry.isVisible())
            .map(entry => entry.id)
            .filter(id => !manifestManager.getManifestWithId(id).defaultHidden);

        const whitelist = this.containerEntries()
            .filter(entry => entry.isVisible())
            .map(entry => entry.id)
            .filter(id => manifestManager.getManifestWithId(id).defaultHidden);

        const homeConfig: HomeConfig = {
            sortOrder,
            blacklist,
            whitelist
        };

        await homeConfiguration.save(this.myFlow, environment.get(environment.keys.productId), homeConfig);

        homeConfiguration.loadFromObject(homeConfig);

        this.closeCallback(true);
    }

    onCancel() {
        this.closeCallback(false);
    }

    onToggleAll() {
        this.setVisibilityAll(!this.allVisible());
    }

    async onReset() {
        await homeConfiguration.reset(this.myFlow, environment.get(environment.keys.productId));
        this.containerEntries(this.getSettings());
        this.myFlow.publish(config.topics.homescreenRequestUpdate);
    }

    setVisibilityAll(visible: boolean) {
        this.containerEntries().forEach(entry => entry.isVisible(visible));
    }

    getDimensions(_maxWidth: number, maxHeight: number) {
        return {
            width: 500,
            // Magic number
            height: Math.min(maxHeight - 32, 252 + 51 * this.containerEntries().length)
        };
    }

    commands = ko.observableArray<ICommand>([
        {
            text: this.myFlow.translate("FlowHtml.Translations.Cancel"),
            click: () => this.onCancel()
        },
        {
            text: this.myFlow.translate("FlowHtml.Translations.Save"),
            click: () => this.onSave(),
            buttonClass: "btn-success"
        }
    ]);
}
